import React, { useState } from "react";
import emailjs from '@emailjs/browser';
import "./orderForm.css";

function OrderForm() {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    phone: '',
    address: '',
    blueberry: 0,
    cola: 0,
    blueLemonade: 0,
    lime: 0,
    orange: 0,
    pineapple: 0,
    raspberry: 0,
    pinaColada: 0,
    margarita: 0,
    strawberryDaiquiri: 0,
    feijoaDaiquiri: 0,
    notes: ''
  });

  const [status, setStatus] = useState({
    message: '',
    isError: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: typeof prevState[name as keyof typeof formData] === 'number' ? Number(value) : value
    }));
  };

  const formatOrderDetails = () => {
    const productNames: { [key: string]: { name: string; price: number } } = {
      blueberry: { name: 'Blueberry', price: 6 },
      cola: { name: 'Cola', price: 6 },
      blueLemonade: { name: 'Blue Lemonade', price: 6 },
      lime: { name: 'Lime', price: 6 },
      orange: { name: 'Orange', price: 6 },
      pineapple: { name: 'Pineapple', price: 6 },
      raspberry: { name: 'Raspberry', price: 6 },
      pinaColada: { name: 'Pina Colada', price: 12 },
      margarita: { name: 'Margarita', price: 12 },
      strawberryDaiquiri: { name: 'Strawberry Daiquiri', price: 12 },
      feijoaDaiquiri: { name: 'Feijoa Daiquiri', price: 12 }
    };

    let totalCost = 0;
    const orderLines = Object.entries(formData)
      .filter(([key, value]) => typeof value === 'number' && value > 0 && productNames[key])
      .map(([key, value]) => {
        const product = productNames[key];
        const quantity = Number(value);
        const lineTotal = product.price * quantity;
        totalCost += lineTotal;
        return `${product.name} (${quantity} x $${product.price}) = $${lineTotal}`;
      });

    return {
      orderLines: orderLines.join('\n'),
      totalCost: `Total Cost: $${totalCost}`
    };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus({ message: 'Sending order...', isError: false });

    const { orderLines, totalCost } = formatOrderDetails();

    try {
      await emailjs.send(
        'service_5xly8zl', // EmailJS service ID
        'template_wbdkxv2', // EmailJS template ID
        {
          from_email: formData.email,
          from_name: formData.fullName,
          phone: formData.phone,
          address: formData.address,
          order_details: orderLines || 'No items ordered',
          total_cost: totalCost,
          notes: formData.notes || 'No additional notes'
        },
        '5bkdTrDja0m2nx2A0' // EmailJS public key
      );

      setStatus({ message: 'Order sent successfully!', isError: false });
      setFormData({
        email: '',
        fullName: '',
        phone: '',
        address: '',
        blueberry: 0,
        cola: 0,
        blueLemonade: 0,
        lime: 0,
        orange: 0,
        pineapple: 0,
        raspberry: 0,
        pinaColada: 0,
        margarita: 0,
        strawberryDaiquiri: 0,
        feijoaDaiquiri: 0,
        notes: ''
      });
    } catch (error) {
      setStatus({ message: 'Failed to send order. Please try again.', isError: true });
    }
  };

  return (
    <div className="OrderFormContainer">
      <h2>Order Here</h2>
      <form onSubmit={handleSubmit} className="OrderFormFields">
        <div className="CustomerEmailField">
          <label>Email</label>
          <input 
            type="email" 
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email" 
            required
          />
        </div>
        <div className="CustomerInfoFields">
          <div className="OrderInputGroup">
            <label>Full Name</label>
            <input 
              type="text" 
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Full name"
              required
            />
          </div>
          <div className="OrderInputGroup">
            <label>Phone Number</label>
            <input 
              type="tel" 
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Phone Number"
              required
            />
          </div>
        </div>
        <div className="CustomerEmailFields">
          <label>Delivery Address</label>
          <input 
            type="text" 
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder="Delivery Address"
            required
          />
        </div>
        <br />

        <div className="OrderLine">
          <div className="OrderInputGroup">
            <label>Blueberry $6</label>
            <input 
              type="number" 
              name="blueberry"
              value={formData.blueberry}
              onChange={handleInputChange}
              min={0}
            />
          </div>
          <div className="OrderInputGroup">
            <label>Cola $6</label>
            <input 
              type="number"
              name="cola" 
              value={formData.cola}
              onChange={handleInputChange}
              min={0}
            />
          </div>
        </div>

        <div className="OrderLine">
          <div className="OrderInputGroup">
            <label>Blue Lemonade $6</label>
            <input 
              type="number"
              name="blueLemonade"
              value={formData.blueLemonade}
              onChange={handleInputChange}
              min={0}
            />
          </div>
          <div className="OrderInputGroup">
            <label>Lime $6</label>
            <input 
              type="number"
              name="lime"
              value={formData.lime}
              onChange={handleInputChange}
              min={0}
            />
          </div>
        </div>

        <div className="OrderLine">
          <div className="OrderInputGroup">
            <label>Orange $6</label>
            <input 
              type="number"
              name="orange"
              value={formData.orange}
              onChange={handleInputChange}
              min={0}
            />
          </div>
          <div className="OrderInputGroup">
            <label>Pineapple $6</label>
            <input 
              type="number"
              name="pineapple"
              value={formData.pineapple}
              onChange={handleInputChange}
              min={0}
            />
          </div>
        </div>

        <div className="OrderLine">
          <div className="OrderInputGroup">
            <label>Raspberry $6</label>
            <input 
              type="number"
              name="raspberry"
              value={formData.raspberry}
              onChange={handleInputChange}
              min={0}
            />
          </div>
          <div className="OrderInputGroup">
            <label>Pina Colada $12</label>
            <input 
              type="number"
              name="pinaColada"
              value={formData.pinaColada}
              onChange={handleInputChange}
              min={0}
            />
          </div>
        </div>

        <div className="OrderLine">
          <div className="OrderInputGroup">
            <label>Margarita $12</label>
            <input 
              type="number"
              name="margarita"
              value={formData.margarita}
              onChange={handleInputChange}
              min={0}
            />
          </div>
          <div className="OrderInputGroup">
            <label>Strawberry Daiquiri $12</label>
            <input 
              type="number"
              name="strawberryDaiquiri"
              value={formData.strawberryDaiquiri}
              onChange={handleInputChange}
              min={0}
            />
          </div>
        </div>

        <div className="OrderLine" style={{ width: "50%" }}>
          <div className="OrderInputGroup">
            <label>Feijoa Daiquiri $12</label>
            <input 
              type="number"
              name="feijoaDaiquiri"
              value={formData.feijoaDaiquiri}
              onChange={handleInputChange}
              min={0}
            />
          </div>
        </div>

        <br />
        <div className="OrderNotesArea">
          <label>Additional notes</label>
          <textarea 
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Message"
          />
        </div>

        {status.message && (
          <div className={`status-message ${status.isError ? 'error' : 'success'}`}>
            {status.message}
          </div>
        )}

        <div className="OrderSubmitButton">
          <button type="submit">Submit</button>
        </div>
      </form>
    </div>
  );
}

export default OrderForm;